import type { Cage, Transit } from '../index.h'

import { F } from '@mobily/ts-belt'
import { attach, createEvent, sample, split } from 'effector'
import { profilesModel } from '~/entities/profiles'
import { profilesFeaturesModel } from '~/features/profiles'
import { noop } from '~/shared/helpers'
import { keys } from '~/shared/jail'

/*
 * ### Hallway->Profiles intermediate cage ###
 */

const init = createEvent<Transit>('init')
const getHallwayProfilesSummaryFx = attach({
  effect: profilesModel.getProfilesSummaryFx,
})
const hallwayUpdateProfileActiveStateFx = attach({
  effect: profilesModel.updateProfileActiveStateFx,
})

// out events
const singleActiveProfile = createEvent('singleActiveProfile')
const selectedActiveProfile = createEvent('selectedActiveProfile')
const shouldCreateProfile = createEvent('shouldCreateProfile')
const shouldChooseProfile = createEvent('shouldChooseProfile')
const shouldManuallyEditActiveProfile = createEvent(
  'shouldManuallyEditActiveProfile'
)

export const hallway2profiles = {
  name: 'hallway2profiles',
  key: keys.lost,
  init,
  in: [],
  out: {
    singleActiveProfile,
    shouldCreateProfile,
    selectedActiveProfile,
    shouldChooseProfile,
    shouldManuallyEditActiveProfile,
  },
} satisfies Cage

const OUT = {
  SINGLE_ACTIVE_PROFILE: 0,
  SELECTED_ACTIVE_PROFILE: 1,
  SHOULD_CREATE_PROFILE: 2,
  SHOULD_MANUALLY_EDIT_ACTIVE_PROFILE: 3,
  SHOULD_CHOOSE_PROFILE: 4,
  __AUTO_ACTIVATE_PROFILE__: 5, // intermediate, not out event
  __SHOULD_EDIT_ACTIVE_PROFILE__: 6, // intermediate, not out event
} as const
type OUT = (typeof OUT)[keyof typeof OUT]

const out = createEvent<OUT>()

split({
  source: out,
  match: F.identity<OUT>,
  cases: {
    [OUT.SINGLE_ACTIVE_PROFILE]: singleActiveProfile,
    [OUT.SELECTED_ACTIVE_PROFILE]: selectedActiveProfile,
    [OUT.SHOULD_CREATE_PROFILE]: shouldCreateProfile,
    [OUT.SHOULD_MANUALLY_EDIT_ACTIVE_PROFILE]: shouldManuallyEditActiveProfile,
    [OUT.SHOULD_CHOOSE_PROFILE]: shouldChooseProfile,
  },
})

sample({
  clock: init,
  fn: noop,
  target: getHallwayProfilesSummaryFx,
})

sample({
  clock: getHallwayProfilesSummaryFx.done,
  source: {
    profiles: profilesModel.$optimisticProfiles,
    profile: profilesModel.$activeProfile,
    selectedProfileId: profilesFeaturesModel.$selectedProfileId,
  },
  fn: ({ profiles, profile, selectedProfileId }) => {
    if (profiles.length === 0) {
      return OUT.SHOULD_CREATE_PROFILE
    }

    if (profiles.length === 1) {
      if (profile != null && profiles[0].id === profile.id) {
        return OUT.SINGLE_ACTIVE_PROFILE
      }

      return OUT.__AUTO_ACTIVATE_PROFILE__
    }

    if (selectedProfileId && profile?.id === selectedProfileId) {
      if (profile.kidsProfile && !profile.kidsProfileTypeId) {
        return OUT.__SHOULD_EDIT_ACTIVE_PROFILE__
      }

      return OUT.SELECTED_ACTIVE_PROFILE
    }

    return OUT.SHOULD_CHOOSE_PROFILE
  },
  target: out,
})

sample({
  clock: out,
  source: profilesModel.$optimisticProfiles,
  filter: (_, reason) => reason === OUT.__AUTO_ACTIVATE_PROFILE__,
  fn: (profiles) => profiles[0].id!,
  target: hallwayUpdateProfileActiveStateFx,
})

// automatically select profile when there is only one profile
sample({
  clock: out,
  source: profilesModel.$optimisticProfiles,
  filter: (_, reason) => reason === OUT.SINGLE_ACTIVE_PROFILE,
  fn: (profiles) => profiles[0].id!,
  target: profilesFeaturesModel.selectedProfileId,
})

sample({
  clock: hallwayUpdateProfileActiveStateFx.done,
  fn: () => OUT.SINGLE_ACTIVE_PROFILE,
  target: out,
})

sample({
  clock: out,
  source: profilesFeaturesModel.$selectedProfile,
  filter: (_, reason) => reason === OUT.__SHOULD_EDIT_ACTIVE_PROFILE__,
  fn: (selectedProfile) => ({ id: selectedProfile!.id }),
  target: profilesFeaturesModel.handleKidsType,
})

sample({
  clock: profilesFeaturesModel.updateProfileFeatureFx.done,
  fn: () => OUT.SELECTED_ACTIVE_PROFILE,
  target: out,
})

sample({
  clock: profilesFeaturesModel.updateProfileFeatureFx.fail,
  fn: () => OUT.SHOULD_CHOOSE_PROFILE,
  target: out,
})

sample({
  clock: profilesFeaturesModel.handleEmptyKidsMultipleTypes,
  fn: () => OUT.SHOULD_MANUALLY_EDIT_ACTIVE_PROFILE,
  target: out,
})
